/**
 * Core functionality for the AlphaFi SDK
 */
import { Blockchain } from '../models/blockchain.js';
import { Protocol } from '../models/protocol.js';
import { Portfolio } from '../models/portfolio.js';
import { poolDetailsMap, poolDetailsMapByPoolName } from '../common/maps.js';
import { depositSingleAssetTxb, depositDoubleAssetTxb, withdrawTxb, coinAmountToXTokensSingleAsset, coinAmountToXTokensDoubleAsset, getAmounts, coinsList, loopingPoolCoinMap, fetchVoloExchangeRate, getInvestor, zapDepositTxb, zapDepositQuoteTxb, getReceipts, claimRewardTxb, } from '@alphafi/alphafi-sdk-upstream';
import { Decimal } from 'decimal.js';
import { conf, CONF_ENV } from '../common/constants.js';
import { AlphaFiReceipt } from '../models/alphafiReceipt.js';
import { AlphaTransactions } from '../models/transactionProtocolModels/alpha.js';
import poolsConfig from '../config/poolsData.js';
import { stSuiExchangeRate, getConf as getStSuiConf } from '@alphafi/stsui-sdk';
import { coinsListByType } from '../common/coinsList.js';
/**
 * Main AlphaFi SDK class providing a simple facade for DeFi operations
 * This is the primary entry point for users of the AlphaFi SDK
 */
export class AlphaFiSDK {
    config;
    // private transactionManager: TransactionManager;
    blockchain;
    protocol;
    portfolio;
    poolLabels;
    constructor(config) {
        this.config = config;
        // Parse and store pool labels from configuration
        this.poolLabels = this.parsePoolLabels(poolsConfig);
        // Initialize core components
        this.blockchain = new Blockchain(config.network);
        this.protocol = new Protocol(config.client, config.network);
        this.portfolio = new Portfolio(this.protocol, this.blockchain, config.client, config.address);
        // Initialize the transaction facade
        // this.transactionManager = new TransactionManager(config.address, this.blockchain);
    }
    /**
     * Deposit assets into a DeFi pool
     * @param options - Deposit configuration options
     * @returns Promise<TransactionResult> - Transaction result
     */
    async deposit(options) {
        const poolInfo = poolDetailsMap[options.poolId];
        if (!poolInfo) {
            throw new Error(`Pool with ID ${options.poolId} not found`);
        }
        if (poolInfo.assetTypes.length === 1) {
            if (poolInfo.poolName === "ALPHA") {
                return await new AlphaTransactions(this.config.address, this.blockchain).depositAlphaTx(options.amount.toString());
            }
            else {
                return await depositSingleAssetTxb(poolInfo.poolName, this.config.address, options.amount.toString());
            }
        }
        else if (poolInfo.assetTypes.length === 2) {
            return await depositDoubleAssetTxb(poolInfo.poolName, this.config.address, options.amount.toString(), options.isAmountA ?? false);
        }
        throw new Error(`Unsupported pool type for pool ${options.poolId}`);
    }
    /**
     * Estimate lp amounts for a DeFi pool
     * @param options - Estimate lp amounts configuration options
     * @returns Promise<[string, string]> - coin amounts
     */
    async estimateLpAmounts(options) {
        const poolInfo = poolDetailsMap[options.poolId];
        if (!poolInfo) {
            throw new Error(`Pool with ID ${options.poolId} not found`);
        }
        if (poolInfo.assetTypes.length === 1) {
            throw new Error(`Pool with ID ${options.poolId} is not a double asset pool`);
        }
        else if (poolInfo.assetTypes.length === 2) {
            return await getAmounts(poolInfo.poolName, options.isAmountA, options.amount, false);
        }
        throw new Error(`Unsupported pool type for pool ${options.poolId}`);
    }
    /**
     * Withdraw assets from a DeFi pool
     * @param options - Withdraw configuration options
     * @returns Promise<TransactionResult> - Transaction result
     */
    async withdraw(options) {
        const poolInfo = poolDetailsMap[options.poolId];
        if (!poolInfo) {
            throw new Error(`Pool with ID ${options.poolId} not found`);
        }
        let xTokens = '0';
        if (options.withdrawMax) {
            const receipt = await getReceipts(poolInfo.poolName, this.config.address, true);
            if (!receipt) {
                throw new Error(`Receipt with ID ${poolInfo.poolId} not found`);
            }
            xTokens = receipt[0].content.fields.xTokenBalance;
        }
        else if (poolDetailsMap[options.poolId].strategyType === 'DOUBLE-ASSET-LOOPING' ||
            poolDetailsMap[options.poolId].strategyType === 'SINGLE-ASSET-LOOPING') {
            const decimals = poolDetailsMap[options.poolId].parentProtocolName === 'NAVI'
                ? 9 - coinsList[loopingPoolCoinMap[poolInfo.poolName].supplyCoin].expo
                : 0;
            let withdrawCoin2Tokens = new Decimal(options.amount).mul(10 ** decimals);
            if (poolDetailsMap[options.poolId].poolName === 'NAVI-LOOP-SUI-VSUI') {
                const voloExchRate = await fetchVoloExchangeRate(true);
                withdrawCoin2Tokens = withdrawCoin2Tokens.div(parseFloat(voloExchRate.data.exchangeRate));
            }
            else if (poolDetailsMap[options.poolId].poolName === 'ALPHALEND-LOOP-SUI-STSUI') {
                const suiTostSuiExchangeRate = await stSuiExchangeRate(getStSuiConf().LST_INFO, true);
                withdrawCoin2Tokens = withdrawCoin2Tokens.div(suiTostSuiExchangeRate);
            }
            const investor_details = (await getInvestor(poolDetailsMap[options.poolId].poolName, true));
            const debtToSupplyRatio = new Decimal(investor_details.content.fields.current_debt_to_supply_ratio);
            const normalisedDebtToSupplyRatio = new Decimal(1).minus(new Decimal(debtToSupplyRatio).div(1e20));
            options.amount = new Decimal(withdrawCoin2Tokens)
                .div(normalisedDebtToSupplyRatio)
                .floor()
                .toString();
            xTokens = await coinAmountToXTokensSingleAsset(options.amount, poolInfo.poolName);
        }
        else if (poolInfo.assetTypes.length === 1) {
            const decimals = poolDetailsMap[options.poolId].parentProtocolName === 'NAVI'
                ? 9 - coinsListByType[poolInfo.assetTypes[0]].expo
                : 0;
            options.amount = new Decimal(options.amount).mul(10 ** decimals).toString();
            xTokens = await coinAmountToXTokensSingleAsset(options.amount, poolInfo.poolName);
        }
        else if (poolInfo.assetTypes.length === 2) {
            xTokens = await coinAmountToXTokensDoubleAsset(options.amount, poolInfo.poolName, options.isAmountA ?? true);
        }
        return await withdrawTxb(xTokens.toString(), poolInfo.poolName, this.config.address);
    }
    async initiateWithdrawAlpha(options) {
        let xtokens = 0;
        const alphaPool = await this.blockchain.getPool(conf[CONF_ENV].ALPHAFI_EMBER_POOL);
        if (options.withdrawMax) {
            const alphafiReceipts = await this.blockchain.getAlphaFiReceipt(this.config.address);
            const receipt = await this.blockchain.getReceiptOld(poolDetailsMapByPoolName['ALPHA'].poolId, this.config.address);
            if (alphafiReceipts.length === 0 && !receipt) {
                throw new Error(`No AlphaFi receipts or receit found for address ${this.config.address}`);
            }
            if (alphafiReceipts.length === 0 && receipt) {
                xtokens = Number(receipt.xTokenBalance);
            }
            else {
                let positionUpdate = alphaPool.recently_updated_alphafi_receipts.find(item => item.key === alphafiReceipts[0].id);
                xtokens = Number(await new AlphaFiReceipt(alphafiReceipts[0], this.blockchain).getTotalShares(conf[CONF_ENV].ALPHAFI_EMBER_POOL)) - Number(positionUpdate ? positionUpdate.value.xtokens_to_remove : 0);
            }
        }
        else {
            xtokens = (new Decimal(options.amount).div(new Decimal(alphaPool.current_exchange_rate).div(1e18))).toNumber();
        }
        return await new AlphaTransactions(this.config.address, this.blockchain).initiateWithdrawAlphaTx(xtokens.toString());
    }
    async claimWithdrawAlpha(ticketId) {
        return await new AlphaTransactions(this.config.address, this.blockchain).claimWithdrawAlphaTx(ticketId);
    }
    async claimAirdrop() {
        return await new AlphaTransactions(this.config.address, this.blockchain).claimAirdropTx();
    }
    /**
     * Get zap deposit quote for a DeFi pool
     * @param options - Zap deposit quote configuration options
     * @returns Promise<[string, string] | undefined> - quote
     */
    async zapDepositQuote(options) {
        const poolInfo = poolDetailsMap[options.poolId];
        if (!poolInfo) {
            throw new Error(`Pool with ID ${options.poolId} not found`);
        }
        return await zapDepositQuoteTxb(options.inputCoinAmount, options.isInputA, poolInfo.poolName, options.slippage);
    }
    /**
     * Zap deposit into a DeFi pool
     * @param options - Zap deposit configuration options
     * @returns Promise<Transaction | undefined> - transaction
     */
    async zapDeposit(options) {
        const poolInfo = poolDetailsMap[options.poolId];
        if (!poolInfo) {
            throw new Error(`Pool with ID ${options.poolId} not found`);
        }
        return await zapDepositTxb(options.inputCoinAmount, options.isInputA, poolInfo.poolName, options.slippage, options.address);
    }
    /**
     * Claim rewards from a DeFi pool
     * @param options - Claim configuration options
     * @returns Promise<TransactionResult> - Transaction result with gas estimate
     */
    async claim(options) {
        return await claimRewardTxb(this.config.address);
        // return this.transactionManager.claim({
        //   poolId: options.poolId,
        // });
    }
    parsePoolLabels(poolsJson) {
        return poolsJson.map((entry) => {
            return {
                ...entry.data,
                strategy_type: entry.strategy_type,
            };
        });
    }
}
//# sourceMappingURL=index.js.map